import { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/lib/auth';
import api from '@/lib/api';
import {
    LayoutDashboard, FileText, Warehouse, ShoppingCart,
    Receipt, BarChart3, Bell, LogOut, Menu, ChevronDown, Users, DollarSign, TrendingUp, ClipboardCheck, Settings, Truck, Database, History
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
    DropdownMenu, DropdownMenuContent,
    DropdownMenuItem, DropdownMenuTrigger
} from '@/components/ui/dropdown-menu';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Sheet, SheetContent, SheetTrigger } from '@/components/ui/sheet';

const navItems = [
    { path: '/', label: 'Dashboard', icon: LayoutDashboard },
    { path: '/purchase-orders', label: 'Purchase Orders', icon: FileText },
    { path: '/inventory', label: 'Inventory', icon: Warehouse },
    { path: '/sales-orders', label: 'Sales Orders', icon: ShoppingCart },
    { path: '/expenses', label: 'Expenses', icon: Receipt },
    { path: '/pnl-report', label: 'P&L Report', icon: BarChart3 },
    { path: '/payments', label: 'Payments', icon: DollarSign },
    { path: '/settlement-summary', label: 'SO Settlements', icon: ClipboardCheck, leaderOrMgmt: true },
    { path: '/sales-analytics', label: 'Sales Analytics', icon: TrendingUp, leaderOrMgmt: true },
    { path: '/notifications', label: 'Notifications', icon: Bell },
    { path: '/settings', label: 'Settings', icon: Settings },
    { path: '/user-management', label: 'Users', icon: Users, mgmtOnly: true },
    { path: '/outbound-explorer', label: 'Outbound Data', icon: Database, mgmtOnly: true },
    { path: '/audit-log', label: 'Audit Log', icon: History, mgmtOnly: true },
];

export default function Layout({ children }) {
    const { user, logout } = useAuth();
    const location = useLocation();
    const navigate = useNavigate();
    const [mobileOpen, setMobileOpen] = useState(false);
    const [unreadCount, setUnreadCount] = useState(0);

    useEffect(() => {
        api.get('/notifications/unread-count')
            .then(res => setUnreadCount(res.data.count))
            .catch(() => { });
    }, [location.pathname]);

    const handleLogout = () => {
        logout();
        navigate('/login');
    };

    const roleBadgeColor = {
        ADMIN: 'bg-green-500/15 text-green-400',
        TEAM_LEADER: 'bg-amber-500/15 text-amber-400',
        MANAGEMENT: 'bg-blue-500/15 text-blue-400',
    };

    const SidebarContent = () => (
        <div className="flex flex-col h-full">
            <div className="p-6 border-b border-white/5">
                <h1 className="text-xl font-bold font-heading text-white tracking-tight" data-testid="app-logo">
                    AgriTrade
                </h1>
                <p className="text-xs text-zinc-500 mt-1 font-medium tracking-wider uppercase">FinOps Platform</p>
            </div>
            <ScrollArea className="flex-1 py-4">
                <nav className="space-y-1 px-3" data-testid="sidebar-nav">
                    {navItems.filter(item => {
                        if (item.mgmtOnly && user?.role !== 'MANAGEMENT') return false;
                        if (item.leaderOrMgmt && !['TEAM_LEADER', 'MANAGEMENT'].includes(user?.role)) return false;
                        return true;
                    }).map(item => {
                        const Icon = item.icon;
                        const active = item.path === '/'
                            ? location.pathname === '/'
                            : location.pathname.startsWith(item.path);
                        return (
                            <Link
                                key={item.path}
                                to={item.path}
                                data-testid={`nav-${item.label.toLowerCase().replace(/[^a-z]/g, '-')}`}
                                className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all duration-150 ${active
                                    ? 'bg-green-500/10 text-green-400 border border-green-500/20'
                                    : 'text-zinc-400 hover:text-zinc-100 hover:bg-white/5 border border-transparent'
                                    }`}
                                onClick={() => setMobileOpen(false)}
                            >
                                <Icon size={18} strokeWidth={1.8} />
                                <span>{item.label}</span>
                                {item.label === 'Notifications' && unreadCount > 0 && (
                                    <Badge className="ml-auto bg-red-500/20 text-red-400 text-xs border-0 px-1.5 py-0">
                                        {unreadCount}
                                    </Badge>
                                )}
                            </Link>
                        );
                    })}
                </nav>
            </ScrollArea>
            <div className="p-4 border-t border-white/5">
                <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                        <button
                            className="flex items-center gap-3 w-full p-2 rounded-lg hover:bg-white/5 transition-colors"
                            data-testid="user-menu-trigger"
                        >
                            <div className="w-8 h-8 rounded-full bg-green-500/20 flex items-center justify-center text-green-400 text-sm font-bold flex-shrink-0">
                                {user?.name?.[0] || 'U'}
                            </div>
                            <div className="flex-1 min-w-0 text-left">
                                <p className="text-sm font-medium text-zinc-100 truncate">{user?.name}</p>
                                <p className={`text-xs px-1.5 py-0.5 rounded inline-block mt-0.5 ${roleBadgeColor[user?.role] || ''}`}>
                                    {user?.role?.replace('_', ' ')}
                                </p>
                            </div>
                            <ChevronDown size={14} className="text-zinc-500" />
                        </button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-48">
                        <DropdownMenuItem onClick={handleLogout} data-testid="logout-btn" className="text-red-400">
                            <LogOut size={14} className="mr-2" />
                            Sign Out
                        </DropdownMenuItem>
                    </DropdownMenuContent>
                </DropdownMenu>
            </div>
        </div>
    );

    return (
        <div className="min-h-screen bg-[#09090B] text-zinc-100 selection:bg-green-500/30">
            {/* Desktop Sidebar */}
            <aside className="hidden md:flex fixed left-0 top-0 bottom-0 w-64 border-r border-white/5 bg-zinc-950/70 backdrop-blur-xl flex-col z-30">
                <SidebarContent />
            </aside>

            {/* Mobile Header */}
            <div className="md:hidden fixed top-0 left-0 right-0 h-14 bg-zinc-950/90 backdrop-blur-xl border-b border-white/5 flex items-center px-4 z-40">
                <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
                    <SheetTrigger asChild>
                        <Button variant="ghost" size="icon" data-testid="mobile-menu-btn">
                            <Menu size={20} />
                        </Button>
                    </SheetTrigger>
                    <SheetContent side="left" className="w-64 p-0 bg-zinc-950 border-r border-white/5">
                        <SidebarContent />
                    </SheetContent>
                </Sheet>
                <h1 className="text-lg font-bold font-heading ml-3 text-white">AgriTrade</h1>
            </div>

            {/* Main Content */}
            <main className="md:ml-64 min-h-screen">
                <div className="px-4 md:px-8 py-6 md:pt-8 pt-20">
                    <div className="max-w-[1600px] mx-auto w-full">
                        {children}
                    </div>
                </div>
            </main>
        </div>
    );
}
